<!--
lc-filters-display-widget

名称：筛选显示插件
描述：显示所有筛选条件。通过该组件，用户会清晰在当前页面的查询状态。并且该组件支持快速删除指定、全部筛选条件
手册：/doc/guide/ListPageQuery.md#17-筛选显示插件-FiltersDisplayWidget

//-->
<template>
  <div v-show="!isEmptyObject(filters)" class="lc-filters-display-widget lc-primary-color lc-secondary-bg-color">
    <el-tag v-for="filter in filters" :key="filter.key" class="m-r-5" size="small" closable :disable-transitions="false" @close="handleClose(filter)">
      <span class="filter-label">{{ locateParamName(filter.key) }}:</span> {{ displayText(filter.key, filter.value, displayTs) }}
    </el-tag>

    <el-button type="primary" plain size="mini" icon="el-icon-delete" class="clear-all-btn" @click="clearAllFilters(bus)">清空</el-button>
  </div>
</template>

<script>
import SearchMixin from '@/mixins/search'
import { ApiSearching, ApiFilterDisplayRefresh } from '@/constants/bus_event'

export default {
  mixins: [SearchMixin],
  props: {
    /**
     * params keys 对应的国际化集
     * 格式：{ keyword: '关键词' }
     *
     * @example paramLocales: this.$t('user.param_keys')
     */
    paramLocales: {
      type: Object,
      default: () => {}
    },
    /**
     * 值显示。只有特殊的才需要展示，默认自动显示 param value
     *   格式： { param key => display text/function({paramKey, paramValue}) }
     *
     * @example valueDisplays: { created_at: ({ paramValue }) => this.displayRangeParam(paramValue) }
     */
    valueDisplays: {
      type: Object,
      default: () => {
        return {}
      }
    },
    /**
     * 通讯的总线
     */
    bus: {
      type: Object,
      default: () => null
    }
  },
  data() {
    return {
      filters: [],
      ignoredParamKeys: ['page', 'per_page'],
      displayTs: null
    }
  },
  computed: {
    /**
     * 完整的参数国际化信息
     *
     * @returns {object} 所有筛选参数的国际化信息
     */
    fullParamLocales() {
      return { ...this.$t('common_component.filters_display_widget.param_keys'), ...this.paramLocales }
    }
  },
  created() {
    // 避免初始化传空值
    if (this.paramLocales === undefined) {
      this.paramLocales = {}
    }

    // Api 搜索启动后，响应（除了响应的方法，其他的基本已经不需要）
    this.bus.$on(ApiSearching, this._apiSearchingProcess)

    // 接听 API 刷新请求，然后触发 API 请求
    this.bus.$on(ApiFilterDisplayRefresh, this._filterDisplayRefreshProcess)
  },
  destroyed() {
    this.bus.$off(ApiSearching, this._apiSearchingProcess)
    this.bus.$off(ApiFilterDisplayRefresh, this._filterDisplayRefreshProcess)
  },
  methods: {
    /**
     * 发送清空筛选的事件
     *
     * @param {string} filterProp 要清空的筛选属性。空则表示全删
     */
    emitClearFilterEvent(filterProp) {
      this.$emit('clear-filter', filterProp)
    },
    handleClose(filter) {
      this.clearFilter(this.bus, filter.key, 'FiltersDisplayWidget')

      this.emitClearFilterEvent(filter.key)
    },
    refresh() {
      this.bus.$emit(ApiFilterDisplayRefresh)
    },
    clearAllFilters() {
      this.clearFilters(this.bus, this.filters, 'FiltersDisplayWidget')

      this.emitClearFilterEvent()
    },
    /**
     * 显示 param value 对应的展示内容
     * 支持一些常规参数值的显示，如：
     * 1. 常规的文本
     * 2. 使用 | 分离的范围值
     *
     * @param {string} paramKey 参数名
     * @param {string} paramValue 参数值
     *
     * @returns {string} 显示的文本
     */
    displayText(paramKey, paramValue) {
      let displayFunc = this.valueDisplays[paramKey]
      if (displayFunc) {
        // 如果有定义展示的方法，则使用定义的方法
        return displayFunc({ paramKey, paramValue })
      }

      // 对常规的参数做一些适配
      // 对范围类型的参数展示
      if (paramValue.includes('|')) {
        let vlaueParts = paramValue.split('|')
        return this.displayRange(vlaueParts[0], vlaueParts[1])
      }

      return paramValue
    },
    /**
     * 显示范围参数
     *
     * @param {string} begin 开始值
     * @param {string} end 结束值
     * @returns {string} begin ~ end
     */
    displayRange(begin, end) {
      if (begin && end) {
        return `${begin} ~ ${end}`
      } else if (begin) {
        return `≥ ${begin}`
      }
      return `≤ ${end}`
    },
    /**
     * API 搜索的同步
     *
     * @param {object} filters
     * @param {object} filters.pagination 分页信息
     *   格式：{page, perPage}
     */
    _apiSearchingProcess({ filters }) {
      this.filters = filters
    },
    /**
     * 重新刷新 filter 的显示。
     * 主要考虑到 filter 的 display 中部分 filter 依赖异步加载，那么异步加载完成后，需要刷新一下。
     */
    _filterDisplayRefreshProcess() {
      this.debug(`[FiltersDisplayWidget] Re-trigger searching event`)
      // 也可以更新 filters 的属性，只刷新修改的
      this.displayTs = moment().valueOf()
    },
    /**
     * 国际化参数名字
     *
     * @param {string} paramName 参数名
     * @returns {string} 国际化信息
     */
    locateParamName(paramName) {
      // 如果参数名是以 _id 结束的，先找 xxx_id，没有的话，再找 xxxx 定义
      return this.fullParamLocales[paramName] || this.fullParamLocales[paramName.substring(0, paramName.length - 3)]
    }
  }
}
</script>

<style lang="scss" scoped>
.lc-filters-display-widget {
  border: 1px solid #e4e7ed;
  padding: 4px;

  .filter-label {
    font-weight: 400;
    opacity: 0.8;
  }

  // border-radius: 5px;
  background-color: #f3f6f8;
  .clear-all-btn {
    border: 0px;
  }
}
</style>
